import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { GlobalSearchProvider } from "@/contexts/GlobalSearchContext";
import SEOProvider from "@/components/SEOProvider";
import AuthenticatedRoute from "@/components/AuthenticatedRoute";
import EmailVerificationGuard from "@/components/EmailVerificationGuard";
import PageLoadingFallback from "@/components/PageLoadingFallback";
import GlobalSearchDialogWrapper from "@/components/search/GlobalSearchDialogWrapper";
import { prefetchCriticalRoutes } from "@/hooks/useRoutePrefetch";

const Index = lazy(() => import("@/pages/Index"));
const Discussions = lazy(() => import("@/pages/Discussions"));
const BibleStudy = lazy(() => import("@/pages/BibleStudy"));
const Prayer = lazy(() => import("@/pages/Prayer"));
const UserBrowse = lazy(() => import("@/pages/UserBrowse"));
const Friends = lazy(() => import("@/pages/Friends"));
const Profile = lazy(() => import("@/pages/Profile"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const Admin = lazy(() => import("@/pages/Admin"));
const Donate = lazy(() => import("@/pages/Donate"));
const BibleExplorer = lazy(() => import("@/pages/BibleExplorer"));
const EmailVerification = lazy(() => import("@/pages/EmailVerification"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const SharePage = lazy(() => import("@/components/social/SharePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Accountability = lazy(() => import("@/pages/Accountability"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => {
  useEffect(() => {
    prefetchCriticalRoutes();
  }, []);

  return (
    <SEOProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <NotificationProvider>
                <GlobalSearchProvider>
                  <GlobalSearchDialogWrapper />
                  <Suspense fallback={<PageLoadingFallback />}>
                    <Routes>
                      <Route path="/" element={
                        <AuthenticatedRoute>
                          <Index />
                        </AuthenticatedRoute>
                      } />
                      <Route path="/verify-email" element={<EmailVerification />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/discussions" element={
                        <EmailVerificationGuard>
                          <Discussions />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/share/:postId" element={
                        <EmailVerificationGuard>
                          <SharePage />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/bible-study" element={
                        <EmailVerificationGuard>
                          <BibleStudy />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/prayer" element={
                        <EmailVerificationGuard>
                          <Prayer />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/accountability" element={
                        <EmailVerificationGuard>
                          <Accountability />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/user-browse" element={
                        <EmailVerificationGuard>
                          <UserBrowse />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/friends" element={
                        <EmailVerificationGuard>
                          <Friends />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/profile/:userId" element={
                        <EmailVerificationGuard>
                          <UserProfile />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/profile" element={
                        <EmailVerificationGuard>
                          <Profile />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/admin" element={
                        <EmailVerificationGuard>
                          <Admin />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/donate" element={
                        <EmailVerificationGuard>
                          <Donate />
                        </EmailVerificationGuard>
                      } />
                      <Route path="/bible-explorer" element={
                        <EmailVerificationGuard>
                          <BibleExplorer />
                        </EmailVerificationGuard>
                      } />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </GlobalSearchProvider>
              </NotificationProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </SEOProvider>
  );
};

export default App;
